<template>
  <AsciiBackground />
  <main :class="[isMobile ? 'mobile' : '']">
    <CLI />
  </main>
  <FooterNav />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import CLI from '@/components/CLI.vue'
import FooterNav from '@/components/FooterNav.vue'
import AsciiBackground from '@/components/AsciiBackground.vue'

import router from '@/router'
import breakpointsStore from '@/store/breakpoints'
import cliUiStore from '@/store/cliUi'
import cliUiRowStore from '@/store/cliRowUi'
import ipStore from '@/store/ip'
import debounce from 'lodash.debounce'

import '@/assets/reset.css'
import '@/assets/variables.css'

export default defineComponent({
  name: 'App',
  components: {
    CLI,
    FooterNav,
    AsciiBackground
  },

  created() {
    ipStore.fetchClientIp()
    breakpointsStore.initilizeBreakPoint()

    setTimeout(() => {
      cliUiStore.isArtificallyLoading.value = false
    }, 1000)
  },

  mounted() {
    document.addEventListener('click', (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const anchor = (event.target as HTMLElement).closest('a')
      if (!anchor || anchor.target === '_blank') return

      const href = anchor.getAttribute('href')
      if (!href || !href.startsWith('/') || href.startsWith('/static')) return

      const resolved = router.resolve(href)
      event.preventDefault()
      if (resolved.fullPath !== router.currentRoute.value.fullPath) {
        router.push(resolved.fullPath)
      }
    })

    const debouncedInit: () => void = debounce((): void => {
      cliUiRowStore.initializeRowWidth()
      cliUiStore.isArtificallyLoading.value = false
    }, 1000)

    window.addEventListener('resize', () => {
      cliUiStore.isArtificallyLoading.value = true

      breakpointsStore.initilizeBreakPoint()
      cliUiStore.initializeWindowRestrictions()
      cliUiStore.initializeDefaultPosition()

      debouncedInit()
    })
  },

  setup() {
    return {
      isMobile: breakpointsStore.isMobile
    }
  }
})
</script>

<style>
html {
  background-color: var(--background-color);
}
html,
body,
#app {
  color: var(--text-color);
  margin: 0;
  padding: 0;
  height: 100%;
}

#app,
input,
button,
pre {
  font-family: 'Anonymous Pro', monospace;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
pre {
  margin: 0;
  font-size: var(--cli-font-size);
  line-height: var(--cli-line-height);
}

main {
  height: calc(var(--main-height) - 2px);
  display: flex;
  align-items: center;
  justify-content: center;
}
main.mobile {
  position: absolute;
  top: calc(var(--footer-height) + 2rem);
  height: calc(100% - var(--footer-height) - var(--cli-palette-height) - 4rem);
  left: 0;
  right: 0;
  overflow: hidden;
}

h1,
h2,
h3,
span.command {
  margin: 0;
  font-size: var(--cli-font-size);
  line-height: var(--cli-line-height);
}
span.hash {
  opacity: 0.5;
}

a {
  color: var(--green-color);
  transition: all 0.1s;
}
a:hover {
  color: var(--dark-green-color);
}
.link {
  color: var(--green-color);
  font-size: 1.2rem;
  transition: all 0.1s;
  cursor: pointer;
  text-decoration: underline;
  user-select: none;
}
.link:hover {
  color: var(--dark-green-color);
}

@media only screen and (max-width: 600px) {
  .link {
    font-size: 1rem;
  }
}
</style>
